export const BU_SERVICE_DESK_ID = '1';

export const BU_JIRA_PORTAL_BASE_URL =
  'https://netsec-jira.engit-alm.com/jira/servicedesk/customer/portal/1/create';

export const SERVICE_OFFERINGS = [
  {
    request_mode: 'pov',
    label: 'POV Assistance',
  },
  {
    request_mode: 'technical_assistance',
    label: 'Technical Assistance',
  },
  {
    request_mode: 'presentation',
    label: 'Briefings & Presentations',
  },
  {
    request_mode: 'automation',
    label: 'Automation Assistance',
  },
  {
    request_mode: 'feature_request',
    label: 'Feature Request',
  },
] as const;

export const REQUEST_TYPE_NAME_TO_CATEGORY = {
  'POV Assistance': 'pov',
  'Design Review': 'design_review',
  'Technical Assistance': 'technical_assistance',
  'Briefings & Presentations': 'presentation',
  'Automation Assistance': 'automation',
  'Feature Request': 'feature_request',
  Documentation: 'documentation',
  Tools: 'tools',
  'Ordering and Licensing': 'ordering_and_licensing',
} as const;

export const BU_REQUEST_TYPE_NAME_TO_MODE = REQUEST_TYPE_NAME_TO_CATEGORY;

export type BuRequestTypeName = keyof typeof REQUEST_TYPE_NAME_TO_CATEGORY;

export type BuRequestMode =
  typeof REQUEST_TYPE_NAME_TO_CATEGORY[BuRequestTypeName];

export const BU_REQUEST_TYPE_NAMES = Object.keys(
  REQUEST_TYPE_NAME_TO_CATEGORY
) as BuRequestTypeName[];

export const isBuRequestTypeName = (value: string): value is BuRequestTypeName => {
  return BU_REQUEST_TYPE_NAMES.includes(value as BuRequestTypeName);
};

export const REQUEST_MODE_TO_REQUEST_TYPE = {
  pov: {
    request_mode: 'pov',
    service_desk_id: '1',
    request_type_id: '123',
    request_type_name: 'POV Assistance',
    request_type_category: 'pov',
  },
  technical_assistance: {
    request_mode: 'technical_assistance',
    service_desk_id: '1',
    request_type_id: '167',
    request_type_name: 'Technical Assistance',
    request_type_category: 'technical_assistance',
  },
  presentation: {
    request_mode: 'presentation',
    service_desk_id: '1',
    request_type_id: '169',
    request_type_name: 'Briefings & Presentations',
    request_type_category: 'presentation',
  },
  automation: {
    request_mode: 'automation',
    service_desk_id: '1',
    request_type_id: '335',
    request_type_name: 'Automation Assistance',
    request_type_category: 'automation',
  },
  feature_request: {
    request_mode: 'feature_request',
    service_desk_id: '1',
    request_type_id: '116',
    request_type_name: 'Feature Request',
    request_type_category: 'feature_request',
  },
} as const;

export type SupportedCatalogRequestMode = keyof typeof REQUEST_MODE_TO_REQUEST_TYPE;

export type SupportedRequestModeConfig = {
  request_mode: SupportedCatalogRequestMode;
  service_desk_id: string;
  request_type_id: string;
  request_type_name: BuRequestTypeName;
  request_type_category: string;
  request_type_config: null;
  request_url: string;
};

const withRequestUrl = (
  mode: SupportedCatalogRequestMode
): SupportedRequestModeConfig => {
  const config = REQUEST_MODE_TO_REQUEST_TYPE[mode];

  return {
    ...config,
    request_type_name: config.request_type_name as BuRequestTypeName,
    request_type_config: null,
    request_url: `${BU_JIRA_PORTAL_BASE_URL}/${config.request_type_id}`,
  };
};

const AUTOMATION_ONLY = ['automation'] as const;
const FULL_SECURITY_SERVICE_SET = [
  'pov',
  'technical_assistance',
  'presentation',
  'automation',
  'feature_request',
] as const;
const POV_PRESENTATION_AUTOMATION = ['pov', 'presentation', 'automation'] as const;
const POV_TECH_PRESENTATION_AUTOMATION = [
  'pov',
  'technical_assistance',
  'presentation',
  'automation',
] as const;
const AUTOMATION_FEATURE = ['automation', 'feature_request'] as const;
const PRESENTATION_AUTOMATION = ['presentation', 'automation'] as const;

export type ProductServiceCatalogEntry = {
  product_id: number;
  product_name: string;
  supported_request_modes: SupportedRequestModeConfig[];
};

const buildProduct = (
  product_id: number,
  product_name: string,
  modes: readonly SupportedCatalogRequestMode[]
): ProductServiceCatalogEntry => ({
  product_id,
  product_name,
  supported_request_modes: modes.map(withRequestUrl),
});

export const BU_PRODUCT_SERVICE_CATALOG: ProductServiceCatalogEntry[] = [
  buildProduct(1, 'Cisco Identity Services Engine', AUTOMATION_ONLY),
  buildProduct(2, 'Cisco Secure Firewall', FULL_SECURITY_SERVICE_SET),
  buildProduct(3, 'Cisco Secure Endpoint', AUTOMATION_ONLY),
  buildProduct(6, 'Cisco Umbrella', FULL_SECURITY_SERVICE_SET),
  buildProduct(7, 'Cisco Secure Email Gateway', AUTOMATION_ONLY),
  buildProduct(10, 'Cisco Duo', POV_PRESENTATION_AUTOMATION),
  buildProduct(12, 'Cisco Secure Malware Analytics', AUTOMATION_ONLY),
  buildProduct(13, 'Cisco Secure Network Analytics', AUTOMATION_ONLY),
  buildProduct(14, 'Meraki MX', AUTOMATION_ONLY),
  buildProduct(15, 'Cisco Secure Web Appliance', FULL_SECURITY_SERVICE_SET),
  buildProduct(16, 'Cisco Secure Workload', FULL_SECURITY_SERVICE_SET),
  buildProduct(17, 'Cisco Secure Email Threat Defense', AUTOMATION_ONLY),
  buildProduct(18, 'Cisco Secure Access', POV_TECH_PRESENTATION_AUTOMATION),
  buildProduct(19, 'Cisco Telemetry Broker', AUTOMATION_ONLY),
  buildProduct(20, 'Cisco Identity Intelligence', POV_PRESENTATION_AUTOMATION),
  buildProduct(21, 'Cisco Attack Surface Management', AUTOMATION_ONLY),
  buildProduct(22, 'Cisco Security Connector', AUTOMATION_ONLY),
  buildProduct(23, 'Cisco Secure WAF', AUTOMATION_ONLY),
  buildProduct(24, 'Cisco Secure DDoS Protection', AUTOMATION_ONLY),
  buildProduct(25, 'Cisco Cyber Vision', AUTOMATION_ONLY),
  buildProduct(26, 'Cisco Secure Equipment Access', AUTOMATION_ONLY),
  buildProduct(27, 'Cisco Vulnerability Management', AUTOMATION_ONLY),
  buildProduct(28, 'Meraki Systems Manager', AUTOMATION_ONLY),
  buildProduct(29, 'Cisco Secure Client', AUTOMATION_ONLY),
  buildProduct(30, 'Talos Incident Response Service', AUTOMATION_ONLY),
  buildProduct(31, 'Cloud Data Protection Assessment', AUTOMATION_ONLY),
  buildProduct(32, 'Cloud Data Architecture Assessment', AUTOMATION_ONLY),
  buildProduct(33, 'Cloud Security Strategy', AUTOMATION_ONLY),
  buildProduct(34, 'Cloud User Entity Behavior Assessment', AUTOMATION_ONLY),
  buildProduct(35, 'Cisco XDR', AUTOMATION_ONLY),
  buildProduct(40, 'Cisco Technical Security Assessment Service', AUTOMATION_ONLY),
  buildProduct(70, 'Cisco Multicloud Defense', POV_TECH_PRESENTATION_AUTOMATION),
  buildProduct(71, 'Security Program Maturity Assessment', AUTOMATION_ONLY),
  buildProduct(72, 'Industrial Control Systems Security Assessment', AUTOMATION_ONLY),
  buildProduct(73, 'Security Segmentation Architecture Design', AUTOMATION_ONLY),
  buildProduct(74, 'Board and Executive Metrics', AUTOMATION_ONLY),
  buildProduct(75, 'Security Metrics Program Development', AUTOMATION_ONLY),
  buildProduct(77, 'Cisco Defense Orchestrator', AUTOMATION_ONLY),
  buildProduct(78, 'Cloudlock', AUTOMATION_ONLY),
  buildProduct(79, 'Cisco HyperShield', AUTOMATION_FEATURE),
  buildProduct(161, 'Cisco Secure Firewall Management Center', AUTOMATION_ONLY),
  buildProduct(165, 'Cisco Security Cloud Control', AUTOMATION_ONLY),
  buildProduct(201, 'Cisco AI Defense', PRESENTATION_AUTOMATION),
];

export const BU_SERVICE_CATALOG = {
  service_offerings: SERVICE_OFFERINGS,
  products: BU_PRODUCT_SERVICE_CATALOG,
  request_type_field_map: {
    product: 'customfield_10216',
    request_type: 'customfield_10217',
    customer_request_type: 'customfield_10109',
    automation_task_needed: 'customfield_12901',
    team: 'customfield_10903',
    geo: 'customfield_10504',
    business_impact: 'customfield_13500',
    your_role: 'customfield_12100',
    assignee_note: 'customfield_10902',
    sfdc_opportunity_link: 'customfield_11409',
    start_date: 'customfield_10422',
    end_date: 'customfield_10423',
    forecast_status: 'customfield_10412',
    sales_stage: 'customfield_10512',
    customer_name: 'customfield_10400',
    pov_type: 'customfield_14452',
    participants: 'customfield_10108',
    segment: 'customfield_12918',
    expected_netsec_k: 'customfield_10405',
    expected_cloudsec_k: 'customfield_14361',
    expected_total_k: 'customfield_10407',
    product_line_area: 'customfield_12013',
    product_qualified: 'customfield_11624',
    latest_comment: 'customfield_10511',
    have_sfdc_deal_id: 'customfield_12803',
    sfdc_fiscal_period: 'customfield_10424',
  },
  request_type_name_to_category: REQUEST_TYPE_NAME_TO_CATEGORY,
  request_type_config: {},
} as const;

export const REQUEST_TYPE_FIELD_MAP = BU_SERVICE_CATALOG.request_type_field_map;

export const REQUEST_TYPE_FIELD_ID = REQUEST_TYPE_FIELD_MAP.request_type;
export const PRODUCT_FIELD_ID = REQUEST_TYPE_FIELD_MAP.product;
export const CUSTOMER_REQUEST_TYPE_FIELD_ID = REQUEST_TYPE_FIELD_MAP.customer_request_type;
export const AUTOMATION_TASK_NEEDED_FIELD_ID = REQUEST_TYPE_FIELD_MAP.automation_task_needed;
export const TEAM_FIELD_ID = REQUEST_TYPE_FIELD_MAP.team;
export const GEO_FIELD_ID = REQUEST_TYPE_FIELD_MAP.geo;
export const BUSINESS_IMPACT_FIELD_ID = REQUEST_TYPE_FIELD_MAP.business_impact;
export const REQUESTOR_ROLE_FIELD_ID = REQUEST_TYPE_FIELD_MAP.your_role;
export const ASSIGNEE_NOTE_FIELD_ID = REQUEST_TYPE_FIELD_MAP.assignee_note;
export const SFDC_OPPORTUNITY_LINK_FIELD_ID = REQUEST_TYPE_FIELD_MAP.sfdc_opportunity_link;
export const START_DATE_FIELD_ID = REQUEST_TYPE_FIELD_MAP.start_date;
export const END_DATE_FIELD_ID = REQUEST_TYPE_FIELD_MAP.end_date;
export const FORECAST_STATUS_FIELD_ID = REQUEST_TYPE_FIELD_MAP.forecast_status;
export const SALES_STAGE_FIELD_ID = REQUEST_TYPE_FIELD_MAP.sales_stage;
export const CUSTOMER_NAME_FIELD_ID = REQUEST_TYPE_FIELD_MAP.customer_name;
export const POV_TYPE_FIELD_ID = REQUEST_TYPE_FIELD_MAP.pov_type;
export const PARTICIPANTS_FIELD_ID = REQUEST_TYPE_FIELD_MAP.participants;
export const SEGMENT_FIELD_ID = REQUEST_TYPE_FIELD_MAP.segment;
export const EXPECTED_NETSEC_K_FIELD_ID = REQUEST_TYPE_FIELD_MAP.expected_netsec_k;
export const EXPECTED_CLOUDSEC_K_FIELD_ID = REQUEST_TYPE_FIELD_MAP.expected_cloudsec_k;
export const EXPECTED_TOTAL_K_FIELD_ID = REQUEST_TYPE_FIELD_MAP.expected_total_k;
export const PRODUCT_LINE_AREA_FIELD_ID = REQUEST_TYPE_FIELD_MAP.product_line_area;
export const PRODUCT_QUALIFIED_FIELD_ID = REQUEST_TYPE_FIELD_MAP.product_qualified;
export const LATEST_COMMENT_FIELD_ID = REQUEST_TYPE_FIELD_MAP.latest_comment;
export const HAVE_SFDC_DEAL_ID_FIELD_ID = REQUEST_TYPE_FIELD_MAP.have_sfdc_deal_id;
export const SFDC_FISCAL_PERIOD_FIELD_ID = REQUEST_TYPE_FIELD_MAP.sfdc_fiscal_period;

export const AUTOMATION_ASSISTANCE_LABEL = 'Automation Assistance';
export const AUTOMATION_ASSISTANCE_VALUE = '13609';

export const AUTOMATION_ASSISTANCE_INVALID_CREATE_FIELDS = [
  REQUEST_TYPE_FIELD_ID,
  CUSTOMER_NAME_FIELD_ID,
] as const;

export const FIELD_VALUE_ALIASES = {
  product: PRODUCT_FIELD_ID,
  product_id: PRODUCT_FIELD_ID,
  product_option: PRODUCT_FIELD_ID,
  product_option_id: PRODUCT_FIELD_ID,
  jira_product: PRODUCT_FIELD_ID,
  jira_product_id: PRODUCT_FIELD_ID,

  request_type: REQUEST_TYPE_FIELD_ID,
  request_type_id: REQUEST_TYPE_FIELD_ID,
  firestarter_request_type: REQUEST_TYPE_FIELD_ID,
  firestarter_request_type_id: REQUEST_TYPE_FIELD_ID,

  customer_request_type: CUSTOMER_REQUEST_TYPE_FIELD_ID,
  automation_task_needed: AUTOMATION_TASK_NEEDED_FIELD_ID,

  role: REQUESTOR_ROLE_FIELD_ID,
  role_id: REQUESTOR_ROLE_FIELD_ID,
  your_role: REQUESTOR_ROLE_FIELD_ID,
  your_role_id: REQUESTOR_ROLE_FIELD_ID,
  requestor_role: REQUESTOR_ROLE_FIELD_ID,
  requestor_role_id: REQUESTOR_ROLE_FIELD_ID,

  business_impact: BUSINESS_IMPACT_FIELD_ID,
  business_impact_id: BUSINESS_IMPACT_FIELD_ID,

  customer_name: CUSTOMER_NAME_FIELD_ID,
  sfdc_opportunity_link: SFDC_OPPORTUNITY_LINK_FIELD_ID,
  opportunity_link: SFDC_OPPORTUNITY_LINK_FIELD_ID,

  team: TEAM_FIELD_ID,
  team_id: TEAM_FIELD_ID,
  geo: GEO_FIELD_ID,
  geo_id: GEO_FIELD_ID,

  forecast_status: FORECAST_STATUS_FIELD_ID,
  sales_stage: SALES_STAGE_FIELD_ID,
  sales_stage_id: SALES_STAGE_FIELD_ID,
  sfdc_fiscal_period: SFDC_FISCAL_PERIOD_FIELD_ID,
  have_sfdc_deal_id: HAVE_SFDC_DEAL_ID_FIELD_ID,

  pov_type: POV_TYPE_FIELD_ID,
  participants: PARTICIPANTS_FIELD_ID,
  segment: SEGMENT_FIELD_ID,

  expected_netsec_k: EXPECTED_NETSEC_K_FIELD_ID,
  expected_cloudsec_k: EXPECTED_CLOUDSEC_K_FIELD_ID,
  expected_total_k: EXPECTED_TOTAL_K_FIELD_ID,

  product_line_area: PRODUCT_LINE_AREA_FIELD_ID,
  product_qualified: PRODUCT_QUALIFIED_FIELD_ID,
  latest_comment: LATEST_COMMENT_FIELD_ID,
  assignee_note: ASSIGNEE_NOTE_FIELD_ID,
} as const;

export const JIRA_FIELD_DEFINITIONS = [
  {
    fieldId: 'summary',
    label: 'Summary',
    type: 'text',
    required: true,
  },
  {
    fieldId: 'description',
    label: 'Description',
    type: 'textarea',
    required: true,
  },
  {
    fieldId: PRODUCT_FIELD_ID,
    label: 'Product',
    type: 'select',
    required: false,
  },
  {
    fieldId: REQUEST_TYPE_FIELD_ID,
    label: 'Request Type',
    type: 'select',
    required: true,
  },
  {
    fieldId: CUSTOMER_REQUEST_TYPE_FIELD_ID,
    label: 'Customer Request Type',
    type: 'select',
    required: false,
  },
  {
    fieldId: AUTOMATION_TASK_NEEDED_FIELD_ID,
    label: 'Automation Task Needed',
    type: 'select',
    required: false,
  },
  {
    fieldId: TEAM_FIELD_ID,
    label: 'Team',
    type: 'select',
    required: false,
  },
  {
    fieldId: GEO_FIELD_ID,
    label: 'GEO',
    type: 'select',
    required: false,
  },
  {
    fieldId: BUSINESS_IMPACT_FIELD_ID,
    label: 'Business Impact',
    type: 'select',
    required: true,
    options: [
      { id: '14301', value: '14301', label: 'Retain' },
      { id: '14302', value: '14302', label: 'Grow/Extend' },
      { id: '14303', value: '14303', label: 'Competitive Takeout' },
    ],
  },
  {
    fieldId: REQUESTOR_ROLE_FIELD_ID,
    label: 'Your Role',
    type: 'select',
    required: false,
    options: [
      { id: '12800', value: '12800', label: 'Pre-Sales (Security Specialist)' },
      { id: '12802', value: '12802', label: 'Post-Sales (Technical Support)' },
    ],
  },
  {
    fieldId: ASSIGNEE_NOTE_FIELD_ID,
    label: 'Assignee Note',
    type: 'textarea',
    required: false,
  },
  {
    fieldId: SFDC_OPPORTUNITY_LINK_FIELD_ID,
    label: 'SFDC Opportunity Link',
    type: 'url',
    required: false,
  },
  {
    fieldId: START_DATE_FIELD_ID,
    label: 'Start Date',
    type: 'date',
    required: false,
  },
  {
    fieldId: END_DATE_FIELD_ID,
    label: 'End Date',
    type: 'date',
    required: false,
  },
  {
    fieldId: FORECAST_STATUS_FIELD_ID,
    label: 'Forecast Status',
    type: 'text',
    required: false,
  },
  {
    fieldId: SALES_STAGE_FIELD_ID,
    label: 'Sales Stage',
    type: 'select',
    required: false,
  },
  {
    fieldId: CUSTOMER_NAME_FIELD_ID,
    label: 'Customer Name',
    type: 'text',
    required: false,
  },
  {
    fieldId: POV_TYPE_FIELD_ID,
    label: 'POV Type',
    type: 'select',
    required: false,
  },
  {
    fieldId: PARTICIPANTS_FIELD_ID,
    label: 'Participants',
    type: 'userpicker',
    required: false,
  },
  {
    fieldId: SEGMENT_FIELD_ID,
    label: 'Segment',
    type: 'select',
    required: false,
  },
  {
    fieldId: EXPECTED_NETSEC_K_FIELD_ID,
    label: 'Expected NetSec K',
    type: 'number',
    required: false,
  },
  {
    fieldId: EXPECTED_CLOUDSEC_K_FIELD_ID,
    label: 'Expected CloudSec K',
    type: 'number',
    required: false,
  },
  {
    fieldId: EXPECTED_TOTAL_K_FIELD_ID,
    label: 'Expected Total K',
    type: 'number',
    required: false,
  },
  {
    fieldId: PRODUCT_LINE_AREA_FIELD_ID,
    label: 'Product Line Area',
    type: 'select',
    required: false,
  },
  {
    fieldId: PRODUCT_QUALIFIED_FIELD_ID,
    label: 'Product Qualified',
    type: 'select',
    required: false,
  },
  {
    fieldId: LATEST_COMMENT_FIELD_ID,
    label: 'Latest Comment',
    type: 'textarea',
    required: false,
  },
  {
    fieldId: HAVE_SFDC_DEAL_ID_FIELD_ID,
    label: 'Have SFDC Deal ID',
    type: 'boolean',
    required: false,
  },
  {
    fieldId: SFDC_FISCAL_PERIOD_FIELD_ID,
    label: 'SFDC Fiscal Period',
    type: 'text',
    required: false,
  },
] as const;

export const DEFAULT_BU_FIELD_VALUES = {
  business_impact: '14301',
  your_role: '12800',
};

export const DEFAULT_BU_FIELD_VALUES_RAW = {
  [BUSINESS_IMPACT_FIELD_ID]: '14301',
  [REQUESTOR_ROLE_FIELD_ID]: '12800',
};

export const getDefaultFieldValuesForRequestType = (
  requestTypeName: string
): Record<string, string> => {
  if (requestTypeName === AUTOMATION_ASSISTANCE_LABEL) {
    return {
      business_impact: '14301',
      your_role: '12800',
    };
  }

  return {
    business_impact: '14301',
    your_role: '12800',
  };
};

export const getProductServiceCatalogEntry = (
  productId: number | ''
): ProductServiceCatalogEntry | null => {
  if (productId === '') return null;

  return (
    BU_PRODUCT_SERVICE_CATALOG.find((entry) => entry.product_id === productId) ||
    null
  );
};

export const getSupportedRequestModesForProduct = (
  productId: number | ''
): SupportedRequestModeConfig[] => {
  return getProductServiceCatalogEntry(productId)?.supported_request_modes || [];
};

export const getSupportedRequestTypeNamesForProduct = (
  productId: number | ''
): BuRequestTypeName[] => {
  return getSupportedRequestModesForProduct(productId)
    .map((item) => item.request_type_name)
    .filter(isBuRequestTypeName);
};

export const getRequestModeConfigForProductAndRequestType = (
  productId: number | '',
  requestTypeName: string
): SupportedRequestModeConfig | null => {
  if (!requestTypeName) return null;

  return (
    getSupportedRequestModesForProduct(productId).find(
      (item) => item.request_type_name === requestTypeName
    ) || null
  );
};

export const getRequestModeConfigByRequestTypeName = (
  requestTypeName: string
): SupportedRequestModeConfig | null => {
  if (!isBuRequestTypeName(requestTypeName)) return null;

  const requestMode = REQUEST_TYPE_NAME_TO_CATEGORY[requestTypeName];

  if (!(requestMode in REQUEST_MODE_TO_REQUEST_TYPE)) {
    return null;
  }

  return withRequestUrl(requestMode as SupportedCatalogRequestMode);
};

export const getInlineConfigDefaultsForProductRequestType = (
  productId: number | '',
  requestTypeName: string
) => {
  const productSpecificConfig =
    getRequestModeConfigForProductAndRequestType(productId, requestTypeName);

  const genericConfig =
    productSpecificConfig || getRequestModeConfigByRequestTypeName(requestTypeName);

  return {
    service_desk_id: genericConfig?.service_desk_id || BU_SERVICE_DESK_ID,
    project_id: '',
    project_key: '',
    project_name: '',
    request_type_id: genericConfig?.request_type_id || '',
    request_create_key: '',
    default_field_values_text: JSON.stringify(
      getDefaultFieldValuesForRequestType(requestTypeName),
      null,
      2
    ),
    priority: 100,
    notes: genericConfig?.request_url
      ? `Request URL: ${genericConfig.request_url}`
      : '',
  };
};